/**
 * Desktop Game of the year (INVENTORY-DESKTOP §3.6, §5; prototype 319-414).
 * Lane D3 — the second HTML5 drag surface, and the only truly two-column
 * screen: the tier board (`flex:2; min-width:340px`) BESIDE the 320px TO-DO
 * rail. Those numbers plus the 22px gap and the 2×26px gutters are exactly
 * 760px, which is why 760 is the breakpoint (§10.3).
 *
 * DRAG (§5.1) — three sources, `TIERS.length + 1` targets:
 *   sources   every tier capsule · the featured TO-DO cover
 *   targets   every tier row (ordered drops) · the TO-DO rail (unplace)
 * The rail's drop is order-less, so it uses `zoneProps`; it goes dashed accent-2
 * only when the dragged game is actually PLACED, because dragging the featured
 * cover back onto its own rail is a no-op.
 *
 * The queue is untouched from mobile: `gotyQueue` orders unplaced potentials by
 * insertion and pushes skips to the back, `gFeat` is `queue[0]`, and placing or
 * skipping advances it as a consequence of the store write. This screen tracks
 * no cursor.
 *
 * ROW HINTS come from `GOTY_ROW_HINTS_DESKTOP` (`DROP …`), never from
 * `gRows[].hint`, which holds mobile's `TAP A TIER BUTTON ABOVE …` copy — the
 * desktop rows are drop targets and say so (§8).
 *
 * TWO PROTOTYPE DEAD BRANCHES, NOT REPRODUCED (see docs/porting/D3.md):
 *   - the featured card's `NEW` badge reads `gFeat.isNew`, which nothing ever
 *     sets — GOTY potentials are a bare id array with no added-at or added-by,
 *     so the badge can never render;
 *   - `steamSub` is a mobile sub-line; desktop moved the same copy into the
 *     pill's tooltip, so the sub-line is gone.
 */

import { SearchBox } from '../../components/SearchBox.tsx';
import { useQueueArt } from '../../components/useArt.ts';
import type { GameId, Tier } from '../../lib/types.ts';
import { useQd } from '../../store/index.ts';
import {
  gotySteamTitle,
  GOTY_SKIP_TIP,
  GOTY_TODO_RAIL_TITLE,
  isPlacedTier,
  rowMinH,
  tierDropRowIds,
  dragRing,
  dragRing2,
  tierRowBord,
  todoRailBord,
} from '../../store/selectors.d3.ts';
import { GOTY_ROW_HINTS_DESKTOP, GOTY_TIER_TIPS } from '../../store/selectors.desktop.ts';
import { gotyVM } from '../../store/selectors.ts';
import BoardCapsule from '../components/BoardCapsule.tsx';
import FilterBar from '../components/FilterBar.tsx';
import PageBar from '../components/PageBar.tsx';
import { TodoThumb } from '../../components/TodoThumb.tsx';
import { TodoFeaturedCover } from '../components/TodoPanel.tsx';
import { beforeIdToIdx } from '../dragHtml5.ts';
import type { BeforeId } from '../dragHtml5.ts';
import { useSearchInputRef } from '../searchHotkey.ts';
import { useHtml5BoardDnd } from '../useHtml5BoardDnd.ts';
import type { BoardDnd } from '../useHtml5BoardDnd.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

type GotyVM = ReturnType<typeof gotyVM>;
type TierGame = GotyVM['gRows'][number]['games'][number];

/** A tier capsule — same §5.5 feedback table as Interests, 8px tighter gaps. */
function TierTile({
  g,
  dnd,
  rowId,
  rowIds,
}: {
  g: TierGame;
  dnd: BoardDnd;
  rowId: string;
  rowIds: readonly GameId[];
}): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const vis = dnd.capVis(rowId, rowIds, g.id);

  return (
    <BoardCapsule
      id={g.id}
      nameUp={g.nameUp}
      fall={g.fall}
      ini={g.ini}
      coverCands={g.coverCands}
      dnd={dnd}
      shift={vis.shift}
      onClick={() => openGame(g.id)}
      style={{
        width: vis.capW,
        marginLeft: vis.capMl,
        border: vis.bord,
        opacity: vis.op,
        transition: vis.trans,
        cursor: 'grab',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '5px 6px',
          fontFamily: MONO,
          fontSize: '8.5px',
          color: 'var(--gt-text-3)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {g.nameUp}
      </div>
    </BoardCapsule>
  );
}

export default function Goty(): JSX.Element {
  const s = useQd();
  const v = gotyVM(s);
  // `/` target while GOTY is up. NOT the ADD A POTENTIAL box above it — that
  // one writes to the crew's list, this one narrows what is already on it.
  const bqRef = useSearchInputRef();
  const dnd = useHtml5BoardDnd();

  // Same art fetch the mobile board does, for the same reason — see the long
  // note in `screens/Goty.tsx`. A GOTY potential is routinely on neither the
  // shared list nor the head of the catalog, which are the only two sets
  // `revalidateVisible` covers, so nothing else would ever fetch its art.
  useQueueArt(v.gRows.flatMap((r) => r.games.map((g) => g.id)));

  /** Read the store fresh — see the note on Interests' `drop`. */
  const dropTier = (t: Tier, id: GameId, before: BeforeId): void => {
    const st = useQd.getState();
    void st.placeTierAt(id, t, beforeIdToIdx(tierDropRowIds(st, t), id, before));
  };

  /** `gSkip` (§4.4): the last unplaced game cannot be rotated away. */
  const skip = (): void => {
    if (v.gTodoN <= 1) {
      s.toast('LAST ONE — PLACE IT OR LEAVE IT');
      return;
    }
    void s.skipTodoId(v.gFeat.id);
  };

  return (
    <div data-screen-label="Game of the year" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Full screen since DESIGN-CHANGELOG 2026-09-03 (see MY INTERESTS). The
          year lives in the title until "one list per year" lands (AA phase 5),
          when a year row joins this first body row beside AUTO-FILL. */}
      <PageBar
        title={
          <>
            GAME OF THE YEAR {v.gotyYear}
            <span style={{ color: 'var(--acc2)' }}>?</span>
          </>
        }
      />
      {/* steam auto-fill — the page's own first row ------------------------ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 26px 0', flexWrap: 'wrap' }}>
        {/* ONE LIST PER YEAR (AA phase 5): the years you have a list for,
            then + ANOTHER YEAR ▾ back to 1990. */}
        <div style={{ display: 'flex', alignItems: 'center', fontFamily: MONO, fontSize: 10, position: 'relative' }}>
          {v.yearRow.map((y) => (
            <span
              key={y.key}
              onClick={() => void s.setGotyYear(y.y)}
              style={{ border: '1px solid ' + y.bd, color: y.clr, background: y.bg, padding: '8px 12px', marginLeft: -1, position: 'relative', zIndex: y.on ? 1 : 0, cursor: 'pointer' }}
            >
              {y.l}
            </span>
          ))}
          {v.moreYears.length ? (
            <span
              onClick={() => s.toggleGotyYearMenu()}
              className="d-hov-acc"
              style={{ marginLeft: 8, color: 'var(--gt-text-5)', cursor: 'pointer', letterSpacing: '.05em' }}
            >
              + ANOTHER YEAR ▾
            </span>
          ) : null}
          {v.yearMenuOpen ? (
            <div
              style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 30, background: 'var(--gt-panel)', border: '1px solid var(--gt-border-2)', maxHeight: 260, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', minWidth: 240 }}
            >
              {v.moreYears.map((y) => (
                <span key={y} onClick={() => void s.setGotyYear(y)} className="d-hov-acc" style={{ padding: '8px 10px', color: 'var(--gt-text-4)', cursor: 'pointer', textAlign: 'center' }}>
                  {y}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div style={{ flex: 1 }} />
        <div
          onClick={() => void s.steamFill()}
          data-tip={gotySteamTitle(v.canSteam, v.steamChecking)}
          data-tip-pos="r"
          style={{
            border: '1px solid ' + v.steamBd,
            color: v.steamClr,
            fontFamily: MONO,
            fontSize: '10.5px',
            padding: '8px 12px',
            cursor: v.steamBusy ? 'progress' : 'pointer',
          }}
        >
          {v.steamLbl}
        </div>
      </div>

      {/* add a potential --------------------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 26px 0', flexWrap: 'wrap' }}>
        <SearchBox
          value={v.gq}
          onChange={(e) => s.setGq(e.target.value)}
          placeholder="ADD A POTENTIAL…"
          prefix="+"
          w="280px"
          ifs="11.5"
        />
        {v.hasGMatches
          ? v.gMatches.map((m) => (
              <span
                key={m.key}
                onClick={() => void s.addPot(m.id)}
                style={{
                  border: '1px solid var(--acc)',
                  color: 'var(--acc)',
                  fontFamily: MONO,
                  fontSize: 10,
                  padding: '6px 10px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                + {m.name} <span style={{ color: 'var(--gt-faint)' }}>{m.yearTxt}</span>
              </span>
            ))
          : null}
        <div style={{ flex: 1 }} />
        {/* The privacy line rides the stat, as the mirror has it, now that the
            explainer sub-line under the title is gone with the title. */}
        <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-faint)' }}>{v.gPotsTxt} · THE PARTY CAN SEE THIS ON YOUR PAGE</span>
      </div>

      {/* The box shares the Filter Bar's full-width row (§3.6), immediately
          before it. The bar keeps the row's remaining width, so its chip rail
          still has somewhere to wrap into. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 26px 0', flexWrap: 'wrap' }}>
        <SearchBox
          value={v.bq}
          onChange={(e) => s.setBq('goty', e.target.value)}
          onClear={() => s.setBq('goty', '')}
          inputRef={bqRef}
          placeholder="SEARCH POTENTIALS…"
          w="230px"
          pfs={11}
          ifs={10.5}
          pad="7px 10px"
        />
        <div style={{ flex: 1, minWidth: 240 }}>
          <FilterBar />
        </div>
      </div>

      {/* board beside the TO-DO rail --------------------------------------- */}
      <div style={{ display: 'flex', gap: 22, padding: '16px 26px 30px', alignItems: 'stretch', flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 340, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {v.gRows.map((r) => (
            <div
              key={r.key}
              {...dnd.rowProps(r.key, (id, before) => dropTier(r.key, id, before))}
              style={{
                display: 'flex',
                border: tierRowBord(dnd.dragOn),
                // Outline, not a wider border — see `borderArmed` in selectors.d3.
                ...dragRing(dnd.dragOn),
                background: 'var(--gt-card)',
              }}
            >
              <div
                style={{
                  width: 84,
                  flex: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  background: r.labBg,
                  borderRight: '1px solid var(--gt-border)',
                  padding: '12px 6px',
                }}
              >
                <div style={{ fontFamily: PIXEL, fontSize: 15, color: r.bigClr }}>{r.big}</div>
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: '7.5px',
                    letterSpacing: '.08em',
                    color: 'var(--gt-text-5)',
                    textAlign: 'center',
                  }}
                >
                  {r.sub}
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: 10,
                  alignContent: 'flex-start',
                  minHeight: rowMinH(dnd.dragOn, dnd.overRow, r.key),
                  transition: 'min-height .16s ease',
                }}
              >
                {r.games.map((g) => (
                  <TierTile
                    key={g.key}
                    g={g}
                    dnd={dnd}
                    rowId={r.key}
                    rowIds={r.games.map((x) => x.id)}
                  />
                ))}
                {r.isEmpty ? (
                  <div
                    style={{
                      alignSelf: 'center',
                      fontFamily: MONO,
                      fontSize: 10,
                      color: 'var(--gt-ghost)',
                      padding: '14px 2px',
                    }}
                  >
                    {GOTY_ROW_HINTS_DESKTOP[r.key]}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {/* TO-DO rail — itself a drop target: drop a placed game to unplace -- */}
        <div
          {...dnd.zoneProps((id) => void useQd.getState().unplaceTier(id))}
          title={GOTY_TODO_RAIL_TITLE}
          style={{
            width: 320,
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            border: todoRailBord(dnd.dragOn, isPlacedTier(s, dnd.dragId)),
            ...dragRing2(dnd.dragOn && isPlacedTier(s, dnd.dragId)),
            background: 'var(--gt-panel)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              borderBottom: '1px dashed var(--gt-border-3)',
              flex: 'none',
            }}
          >
            <div style={{ fontFamily: PIXEL, fontSize: 7, color: 'var(--gt-text-5)' }}>TO DO</div>
            <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)' }}>ONE AT A TIME</div>
            <div style={{ flex: 1 }} />
            <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--acc2)' }}>{v.gLeftTxt}</div>
          </div>

          {v.hasGFeat ? (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                padding: 14,
              }}
            >
              <TodoFeaturedCover feat={v.gFeat} dnd={dnd} tiltNs="todo" />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  onClick={() => s.openGame(v.gFeat.id)}
                  style={{ fontWeight: 600, fontSize: 14, color: 'var(--gt-text)', cursor: 'pointer' }}
                >
                  {v.gFeat.name}
                </div>
                <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)' }}>{v.gFeat.metaTxt}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                {v.gBtns.map((b) => (
                  <div
                    key={b.key}
                    onClick={() => void s.placeTier(b.id, b.key)}
                    data-tip={GOTY_TIER_TIPS[b.key]}
                    style={{
                      height: 34,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: PIXEL,
                      fontSize: 10,
                      border: '1px solid ' + b.bd,
                      color: b.clr,
                      background: b.bg,
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                  >
                    {b.t}
                  </div>
                ))}
              </div>

              <div
                style={{
                  fontFamily: MONO,
                  fontSize: '8.5px',
                  color: 'var(--gt-tick)',
                  textAlign: 'center',
                  lineHeight: 1.8,
                }}
              >
                {v.gLegendTxt}
                <br />
                TAP A TIER — OR DRAG THE COVER STRAIGHT ONTO A ROW
              </div>

              <div
                onClick={skip}
                data-tip={GOTY_SKIP_TIP}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  border: '1px solid var(--gt-border-2)',
                  color: 'var(--gt-text-5)',
                  fontFamily: MONO,
                  fontSize: 10,
                  letterSpacing: '.05em',
                  padding: '9px 0',
                  cursor: 'pointer',
                }}
              >
                SKIP FOR NOW ↻
              </div>

              {v.hasGUpNext ? (
                <div
                  style={{
                    borderTop: '1px dashed var(--gt-border)',
                    paddingTop: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', color: 'var(--gt-faint)' }}>
                    UP NEXT
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {v.gUpNext.map((n) => (
                      <TodoThumb key={n.key} n={n} w={48} fs={8} />
                    ))}
                    {v.hasGMore ? (
                      <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-tick)' }}>{v.gMoreTxt}</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {v.gAllDone ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: '34px 16px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontFamily: PIXEL, fontSize: 14, color: 'var(--acc)' }}>✓</div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-muted)', lineHeight: 1.9 }}>
                ALL PLACED — ADD MORE POTENTIALS ABOVE
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
